import React, { useState } from "react";
import { 
  Modal, 
  Box, 
  TextField, 
  Typography, 
  IconButton,
  Divider,
  CircularProgress,
  Button
} from "@mui/material";
import { Close, CheckCircle } from "@mui/icons-material";
import axios from "../../axiosInstance";
import CustomButton from "../../Components/CustomButton";
import { message } from "antd";
import { useNavigate } from "react-router-dom";

const TransactionModal = ({ open, onClose, feeId }) => {
  const [txnId, setTxnId] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async () => {
    if (!txnId.trim()) {
      message.error("Please enter a valid transaction ID");
      return;
    }

    setIsLoading(true);
    
    try {
      const res = await axios.post("/api/v1/fees/verify", { 
        feeId, 
        txnId 
      });

      message.success(
        res.data.message || 
        "Verification received! Admin will confirm your payment shortly."
      );
      
      setIsSuccess(true);
      setTimeout(() => {
        onClose();
        setIsSuccess(false);
        setTxnId("");
        navigate("/online-fees"); 
      }, 2000);
    } catch (err) {
      message.error(
        err.response?.data?.message || 
        "Verification failed. Please try again or contact support."
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    onClose();
    setTxnId("");
    setIsSuccess(false);
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: '90%', sm: 450 },
          bgcolor: 'background.paper',
          boxShadow: 24,
          borderRadius: 3,
          p: 4,
          outline: 'none'
        }}
      >
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          mb: 2
        }}>
          <Typography 
            variant="h6"
            sx={{
              fontFamily: "Montserrat, sans-serif",
              fontWeight: 600,
              color: "#065A79",
              fontSize: "1.5rem"
            }}
          >
            Payment Verification
          </Typography>
          <IconButton onClick={handleClose}>
            <Close sx={{ color: "#065A79" }} />
          </IconButton>
        </Box>

        <Divider sx={{ mb: 3 }} />

        {isSuccess ? (
          <Box sx={{ 
            textAlign: 'center',
            py: 4
          }}>
            <CheckCircle 
              sx={{ 
                fontSize: 60, 
                color: '#4CAF50',
                mb: 2
              }} 
            />
            <Typography variant="h6" sx={{ mb: 1, color: '#4CAF50' }}>
              Payment Verified!
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Redirecting to online fees page...
            </Typography>
          </Box>
        ) : (
          <>
            <Typography 
              variant="body1" 
              sx={{ 
                mb: 3,
                color: 'text.secondary'
              }}
            >
              Please enter your UPI transaction ID to verify your payment
            </Typography>

            <TextField
              fullWidth
              variant="outlined"
              margin="normal"
              label="Transaction ID"
              value={txnId}
              onChange={(e) => setTxnId(e.target.value)}
              InputProps={{
                sx: {
                  borderRadius: 2,
                  "& fieldset": {
                    borderColor: "#065A79",
                  },
                  "&:hover fieldset": {
                    borderColor: "#065A79",
                  },
                }
              }}
              InputLabelProps={{
                sx: {
                  color: "#065A79",
                  "&.Mui-focused": {
                    color: "#065A79",
                  },
                }
              }}
            />

            <Box sx={{ mt: 3 }}>
              <CustomButton
                ButtonName={isLoading ? "Verifying..." : "Submit Verification"}
                handleSubmit={handleSubmit}
                disabled={isLoading}
                sx={{
                  width: '100%',
                  py: 1.5,
                  fontSize: '1rem',
                  borderRadius: 2,
                  bgcolor: '#065A79',
                  '&:hover': {
                    bgcolor: '#054862'
                  },
                  '&:disabled': {
                    bgcolor: '#cccccc'
                  }
                }}
              /> 
              {isLoading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                  <CircularProgress size={24} sx={{ color: '#065A79' }} />
                </Box>
              )}
            </Box>
          </>
        )}
      </Box>
    </Modal>
  );
};

export default TransactionModal;